import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    listUgcShares,
    getUgcStats,
    getUgcSyncState,
    triggerUgcSync,
    UgcShare,
    UgcStats,
    UgcSyncState,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import Pagination from '../../components/Pagination';
import ImageLightbox from '../../components/ImageLightbox';
import SyncBar from '../../components/SyncBar';
import { useSyncPolling } from '../../utils/useSyncPolling';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { AuditBadge } from './ugcLabels';
import { ProcessThumb } from './parts';
import { useMModeText } from '../../utils/mmodeText';
import { thumbUrl, THUMB_W_SM } from '../../utils/imgUrl';
import {
    useDictText,
    useProcessTypeOptions,
    useProcessTypeText,
} from '../../utils/dictOptions';
import SearchSelect from '../../components/SearchSelect';

// 数据资产·UGC 分享中心 (2026-09-01, doc/数据资产/对接设计.md):
// 用户社区分享的"参数+效果"记录, 由后端每小时从美服社区库同步 (页面可手动触发).
// 结构同官方参数: 封面图·机型·材料分类·加工模式·具体参数; "是否通过"= 社区准入审核状态.
// 详情为独立路由页 /assets/ugc-shares/:id —— 整行可点跳转, 不单开「操作」列
// (与耗材识别中心/加工数据中心的明细表交互一致).

const AUDIT_STATUS_OPTS = [
    { value: 'approved', label: '已通过' },
    { value: 'pending', label: '待审核' },
    { value: 'rejected', label: '已驳回' },
];
const HAS_WORKED_OPTS = [
    { value: 'yes', label: '有「有用」反馈' },
    { value: 'no', label: '有「没用」反馈' },
];
const RESTORED_OPTS = [
    { value: 'yes', label: '已还原' },
    { value: 'no', label: '仅原始' },
];

const PAGE_SIZE = 20;

// 核心参数 (功率/速度/次数) — 从 paraSnapshot 提取, 其余字段在详情页看全量.
function coreParams(snapshot: string): {
    power?: string;
    speed?: string;
    repeat?: string;
} {
    try {
        const p = JSON.parse(snapshot || '{}') as Record<string, unknown>;
        const pick = (k: string) =>
            p[k] === undefined || p[k] === null ? undefined : String(p[k]);
        return {
            power: pick('power'),
            speed: pick('speed'),
            repeat: pick('repeat'),
        };
    } catch {
        return {};
    }
}

// 统计卡.
function StatCard({
    label,
    value,
    sub,
    accent,
}: {
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    accent?: string;
}) {
    return (
        <div className="card p-4">
            <div className="text-xs text-slate-500">{label}</div>
            <div
                className={`text-2xl font-bold mt-1 ${accent || 'text-slate-800'}`}
            >
                {value}
            </div>
            {sub && (
                <div className="text-[11px] text-slate-400 mt-1">{sub}</div>
            )}
        </div>
    );
}

function UgcShareList() {
    // 加工模式 / 工艺类型 / 光源均从平台字典取名 (mm_mode + dict process_type/light_source).
    const modeText = useMModeText();
    const typeText = useProcessTypeText();
    const typeOptions = useProcessTypeOptions();
    const laserText = useDictText('light_source');
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(
        initQ.get('keyword') || '',
    );
    const [deviceCode, setDeviceCode] = useState(initQ.get('deviceCode') || '');
    const [categoryCode, setCategoryCode] = useState(
        initQ.get('categoryCode') || '',
    );
    const [categoryCodeL2, setCategoryCodeL2] = useState(
        initQ.get('categoryCodeL2') || '',
    );
    const [processingType, setProcessingType] = useState(
        initQ.get('processingType') || '',
    );
    const [auditStatus, setAuditStatus] = useState(
        initQ.get('auditStatus') || '',
    );
    const [hasWorked, setHasWorked] = useState(initQ.get('hasWorked') || '');
    // 耗材还原 (2026-09-23): 已还原的行多一张模型反推的"加工前耗材图", 与封面的加工后效果图成对.
    const [restored, setRestored] = useState(initQ.get('restored') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);

    const [list, setList] = useState<UgcShare[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<UgcStats | null>(null);
    const [syncState, setSyncState] = useState<UgcSyncState | null>(null);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useSyncQueryToUrl('/assets/ugc-shares', {
        keyword,
        deviceCode,
        categoryCode,
        categoryCodeL2,
        processingType,
        auditStatus,
        hasWorked,
        restored,
        page,
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listUgcShares({
                keyword,
                deviceCode,
                categoryCode,
                categoryCodeL2,
                processingType,
                auditStatus,
                hasWorked,
                restored,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.total);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [
        keyword,
        deviceCode,
        categoryCode,
        categoryCodeL2,
        processingType,
        auditStatus,
        hasWorked,
        restored,
        page,
    ]);

    const loadStats = useCallback(async () => {
        try {
            const s = await getUgcStats();
            setStats(s);
            setSyncState(s.syncState || null);
        } catch {
            /* 统计失败不阻塞列表 */
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        loadStats();
    }, [loadStats]);
    // 手动同步: 触发后轮询状态行, 跑完刷新列表与统计.
    const pollUgc = useCallback(async () => {
        const st = await getUgcSyncState();
        setSyncState(st);
        return {
            running: !!st && st.status === 'running',
            error: st?.status === 'failed' ? st.lastError : '',
        };
    }, []);
    const onSynced = useCallback(() => {
        load();
        loadStats();
    }, [load, loadStats]);
    const { syncing, start: onSync } = useSyncPolling({
        trigger: triggerUgcSync,
        poll: pollUgc,
        onDone: onSynced,
    });

    const publishedTotal =
        stats?.byUgcStatus.find((s) => s.key === 'published')?.count ?? 0;
    const approvedTotal =
        stats?.byAuditStatus.find((s) => s.key === 'approved')?.count ?? 0;
    const cat2Options = useMemo(() => {
        if (!stats) return [];
        return stats.categories
            .filter((c) => !categoryCode || c.categoryCode === categoryCode)
            .map((c) => c.categoryCodeL2)
            .filter((v, i, arr) => v && arr.indexOf(v) === i);
    }, [stats, categoryCode]);
    const cat1Options = useMemo(() => {
        if (!stats) return [];
        return stats.categories
            .map((c) => c.categoryCode)
            .filter((v, i, arr) => v && arr.indexOf(v) === i);
    }, [stats]);

    const resetFilters = () => {
        setKeyword('');
        setKeywordDraft('');
        setDeviceCode('');
        setCategoryCode('');
        setCategoryCodeL2('');
        setProcessingType('');
        setAuditStatus('');
        setHasWorked('');
        setRestored('');
        setPage(1);
    };
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    // 卡片头那句"已筛选 / 全部记录"用它 —— ugcStatus 有默认值(published), 不算用户筛选.
    const filtered = Boolean(
        keyword ||
            deviceCode ||
            categoryCode ||
            categoryCodeL2 ||
            processingType ||
            auditStatus ||
            hasWorked ||
            restored,
    );

    return (
        <div className="space-y-5">
            {/* 标题 + 同步操作 */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-base font-semibold">UGC 分享</div>
                    <div className="text-xs text-slate-500">
                        用户社区分享的加工参数 · 结构同官方参数 ·
                        仅同步已发布内容 · 「是否通过」为社区准入审核结果
                    </div>
                </div>
                <SyncBar
                    state={syncState}
                    syncing={syncing}
                    onSync={onSync}
                    sourceText="来源 xtool-community · 每小时自动"
                />
            </div>

            {/* 统计条 */}
            <div className="grid grid-cols-4 gap-4">
                <StatCard
                    label="资产总数 (已发布)"
                    value={publishedTotal.toLocaleString()}
                    sub={`累计同步 ${stats?.byUgcStatus.reduce((a, s) => a + s.count, 0).toLocaleString() ?? '—'} 条`}
                />
                <StatCard
                    label="审核通过"
                    value={approvedTotal.toLocaleString()}
                    accent="text-emerald-600"
                    sub={`待审 ${stats?.byAuditStatus.find((s) => s.key === 'pending')?.count ?? 0} · 驳回 ${stats?.byAuditStatus.find((s) => s.key === 'rejected')?.count ?? 0}`}
                />
                <StatCard
                    label="「有用」回流"
                    value={stats?.workedSum.toLocaleString() ?? '—'}
                    accent="text-brand-600"
                    sub="用户按此参数加工成功的反馈数"
                />
                <StatCard
                    label="「没用」回流"
                    value={stats?.unworkedSum.toLocaleString() ?? '—'}
                    accent="text-rose-500"
                    sub="负反馈数 · 全量行为见详情"
                />
            </div>

            {/* 筛选条 */}
            <div className="card p-3 flex flex-nowrap items-center gap-2 overflow-x-auto">
                <input
                    className="inp"
                    style={{ width: 190, minWidth: 150 }}
                    placeholder="搜 源参数ID / 材料 / 品牌 / 描述 / 作者"
                    title="纯数字会同时按源参数ID与文本匹配；其余按材料/品牌/描述/作者模糊搜"
                    value={keywordDraft}
                    onChange={(e) => setKeywordDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setKeyword(keywordDraft.trim());
                            setPage(1);
                        }
                    }}
                />
                <SearchSelect
                    className="w-36"
                    value={deviceCode}
                    onChange={(v) => {
                        setDeviceCode(v);
                        setPage(1);
                    }}
                    placeholder="机型 (全部)"
                    options={(stats?.devices || []).map((d) => ({
                        value: d.deviceCode,
                        label: `${d.deviceName || d.deviceCode} (${d.n})`,
                    }))}
                    clearable
                />
                <SearchSelect
                    className="w-32"
                    value={categoryCode}
                    onChange={(v) => {
                        setCategoryCode(v);
                        setCategoryCodeL2('');
                        setPage(1);
                    }}
                    placeholder="一级分类 (全部)"
                    options={cat1Options.map((c) => ({ value: c, label: c }))}
                    clearable
                />
                <SearchSelect
                    className="w-32"
                    value={categoryCodeL2}
                    onChange={(v) => {
                        setCategoryCodeL2(v);
                        setPage(1);
                    }}
                    placeholder="二级分类 (全部)"
                    options={cat2Options.map((c) => ({ value: c, label: c }))}
                    clearable
                />
                <SearchSelect
                    className="w-32"
                    value={processingType}
                    onChange={(v) => {
                        setProcessingType(v);
                        setPage(1);
                    }}
                    placeholder="加工类型 (全部)"
                    options={typeOptions.map(({ code, label }) => ({ value: code, label }))}
                    clearable
                />
                <SearchSelect
                    className="w-[124px] shrink-0"
                    value={auditStatus}
                    onChange={(v) => {
                        setAuditStatus(v);
                        setPage(1);
                    }}
                    placeholder="是否通过 (全部)"
                    options={AUDIT_STATUS_OPTS}
                    clearable
                />
                <SearchSelect
                    className="w-[112px] shrink-0"
                    value={hasWorked}
                    onChange={(v) => {
                        setHasWorked(v);
                        setPage(1);
                    }}
                    placeholder="回流 (全部)"
                    options={HAS_WORKED_OPTS}
                    clearable
                />
                <div
                    className="shrink-0"
                    title="已还原 = 有视觉模型从效果图反推出的加工前耗材图(推测, 非实拍)"
                >
                    <SearchSelect
                        className="w-[120px]"
                        value={restored}
                        onChange={(v) => {
                            setRestored(v);
                            setPage(1);
                        }}
                        placeholder="耗材还原 (全部)"
                        options={RESTORED_OPTS}
                        clearable
                    />
                </div>
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                        setKeyword(keywordDraft.trim());
                        setPage(1);
                    }}
                >
                    搜索
                </button>
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={resetFilters}
                >
                    重置
                </button>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* 列表: 卡片头写口径说明 + 窄屏横向滚动, 列不换行不挤压 */}
            <div className="card">
                <div className="flex items-center px-4 pt-3 pb-2">
                    <div className="text-sm font-semibold text-slate-700">
                        分享明细
                    </div>
                    <div className="ml-2 text-xs text-slate-400">
                        {filtered ? '已筛选' : '全部记录'} · 按发布时间倒序
                    </div>
                    <div className="ml-auto text-xs text-slate-400">
                        共 {total.toLocaleString()} 条
                    </div>
                </div>
                {/* 「加工前」这一列的口径说明: 它来自耗材还原, 不是源数据自带的实拍图 —— 不写明白
                    会被当成"这些分享缺图", 实际上是"这条还没做还原"。与「历史加工数据」同款写法。 */}
                <div className="px-4 pb-2 text-[11px] text-slate-400">
                    「加工前」是<b className="text-violet-600">已还原</b>
                    分享才有的耗材还原图（视觉模型把效果图里的加工痕迹抹掉、反推出的加工前样子，
                    <b>推测、非实拍</b>）；未还原的分享该列显示「无」。
                    {/* 条数取实时统计, 不写死 —— 还原是会再跑批次的, 写死的数字迟早过期 */}
                    {stats?.restoredCount != null
                        ? `已还原 ${stats.restoredCount.toLocaleString()} 条。`
                        : ''}
                </div>
                <div className="overflow-x-auto">
                <table
                    className="w-full text-[13px]"
                    style={{ minWidth: 1040 }}
                >
                    <thead>
                        <tr className="text-left text-xs text-slate-400 border-b border-slate-100 whitespace-nowrap">
                            <th className="px-3 py-2.5 font-medium">源参数ID</th>
                            {/* 列头只留两个字: 「推测、非实拍」和"未还原显示无"已经写在卡片头的口径说明里,
                                列头再来一行副标题就是同一句话说两遍, 还把两列撑开、看不出是一组对比。
                                同理不放「仅已还原有」按钮 —— 只看已还原的走筛选区那个「耗材还原」下拉。 */}
                            <th
                                className="px-3 py-2.5 font-medium"
                                title="「加工前」= 耗材还原图: 视觉模型把效果图里的加工痕迹抹掉、反推出的加工前耗材原样(推测, 非实拍)。只有「已还原」的记录有这张图, 其余显示「无」。"
                            >
                                加工前
                            </th>
                            <th
                                className="px-3 py-2.5 font-medium"
                                title="用户上报的加工后效果图 (封面图)"
                            >
                                加工后
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                标题 / 材料
                            </th>
                            <th className="px-3 py-2.5 font-medium">机型</th>
                            <th className="px-3 py-2.5 font-medium">
                                一级分类
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                二级分类
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                加工模式
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                核心参数
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                是否通过
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                加工次数
                            </th>
                            <th className="px-3 py-2.5 font-medium">
                                有用/没用
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={11}
                                    className="text-center text-slate-400 py-10"
                                >
                                    加载中…
                                </td>
                            </tr>
                        ) : list.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={11}
                                    className="text-center text-slate-400 py-10"
                                >
                                    该筛选下暂无数据
                                </td>
                            </tr>
                        ) : (
                            list.map((r) => {
                                const cp = coreParams(r.paraSnapshot);
                                return (
                                    <tr
                                        key={r.id}
                                        className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                                        onClick={() =>
                                            history.push(
                                                `/assets/ugc-shares/${r.id}`,
                                            )
                                        }
                                    >
                                        <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="hover:text-blue-600 hover:underline"
                                                title="按这个源参数ID搜索"
                                                onClick={(e) => {
                                                    // 行点击是进详情, 这里只想搜
                                                    e.stopPropagation();
                                                    const kw = String(r.sourceId);
                                                    setKeywordDraft(kw);
                                                    setKeyword(kw);
                                                    setPage(1);
                                                }}
                                            >
                                                {r.sourceId}
                                            </button>
                                        </td>
                                        {/* 加工前 / 加工后 各占一列, 与「历史加工数据」明细表同款 (ProcessThumb 负责缺图占位) */}
                                        <td className="px-3 py-2">
                                            <ProcessThumb
                                                src={r.restoredImageUrl}
                                                label="加工前 · AI 还原(推测)"
                                                size={48}
                                                onZoom={setZoomSrc}
                                            />
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.coverImage ? (
                                                <img
                                                    src={thumbUrl(r.coverImage, THUMB_W_SM)}
                                                    alt={r.materialName}
                                                    className="rounded-lg object-cover cursor-zoom-in border border-slate-100"
                                                    style={{
                                                        width: 48,
                                                        height: 48,
                                                        minWidth: 48,
                                                    }}
                                                    loading="lazy"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setZoomSrc(
                                                            r.coverImage,
                                                        );
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-lg bg-slate-100"
                                                    style={{
                                                        width: 48,
                                                        height: 48,
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td className="px-3 py-2 max-w-[220px]">
                                            <div
                                                className="font-medium text-slate-700 truncate"
                                                title={r.materialName}
                                            >
                                                {r.materialName || '—'}
                                            </div>
                                            <div className="text-[11px] text-slate-400 truncate">
                                                {[
                                                    r.brand,
                                                    r.thickness != null
                                                        ? `${r.thickness}mm`
                                                        : '',
                                                    r.color,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ') ||
                                                    `by ${r.creatorName || r.creatorId}`}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div>
                                                {r.deviceName ||
                                                    r.deviceCode ||
                                                    '—'}
                                            </div>
                                            <div className="text-[11px] text-slate-400">
                                                {[
                                                    laserText(
                                                        r.laserSourceCode,
                                                        '',
                                                    ),
                                                    r.laserPowerCode
                                                        ? `${r.laserPowerCode}W`
                                                        : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {r.categoryCode || '—'}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {r.categoryCodeL2 || '—'}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="inline-block px-2 py-0.5 rounded border border-brand-200 text-brand-600 bg-brand-50 text-[11px] whitespace-nowrap">
                                                {modeText(r.processingModeCode)}
                                            </span>
                                            <div className="text-[11px] text-slate-400 mt-0.5 whitespace-nowrap">
                                                {typeText(r.processingTypeCode)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                                            {cp.power ||
                                            cp.speed ||
                                            cp.repeat ? (
                                                <>
                                                    {cp.power && (
                                                        <>
                                                            功率=
                                                            <b>
                                                                {cp.power}
                                                            </b>{' '}
                                                        </>
                                                    )}
                                                    {cp.speed && (
                                                        <>
                                                            速度=
                                                            <b>
                                                                {cp.speed}
                                                            </b>{' '}
                                                        </>
                                                    )}
                                                    {cp.repeat && (
                                                        <>
                                                            次数=
                                                            <b>{cp.repeat}</b>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <AuditBadge
                                                status={r.auditStatus}
                                            />
                                        </td>
                                        <td
                                            className={`px-3 py-2 whitespace-nowrap ${r.processCount > 0 ? 'text-sky-600 font-medium' : 'text-slate-400'}`}
                                        >
                                            {r.processCount}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span
                                                className={
                                                    r.workedCount > 0
                                                        ? 'text-emerald-600 font-medium'
                                                        : 'text-slate-400'
                                                }
                                            >
                                                👍 {r.workedCount}
                                            </span>
                                            <span
                                                className={`ml-2 ${r.unworkedCount > 0 ? 'text-rose-500 font-medium' : 'text-slate-400'}`}
                                            >
                                                👎 {r.unworkedCount}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            <div className="flex items-center justify-end text-xs text-slate-500">
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                    total={total}
                />
            </div>

            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default UgcShareList;
